import { z } from 'zod';
import { orgAttachmentSchema } from './org.js';

/**
 * Moving equipment from one organization to another, and deciding what is left
 * of the one it came from.
 *
 * This is an OWNERSHIP change and nothing else. No CLI session is opened, no
 * SNMP set is sent, no ACS parameter is written — a chassis whose row changes
 * tenant is configured exactly as it was a second earlier, still informing to
 * the credentials the old org's slug produced. That is a real consequence and
 * the plan says so rather than the code quietly fixing it, because "re-push
 * this chassis's config" is a decision with an outage in it and this feature is
 * not the place to make it.
 *
 * Two calls, and the split IS the safety design, the same way it is for bulk
 * actions: `plan` computes and writes nothing, `apply` names that plan's id and
 * re-checks every line of it against the database as it stands NOW. A fleet
 * moves; a plan is a description of a moment, never a promise about a later one.
 */

/**
 * What an operator may tick.
 *
 * These are the rows that OWN something. Everything else in the model hangs off
 * one of them — an ONU off its OLT, a PON port off its chassis, a VLAN off the
 * chassis that carries it — and travels without being named, which is why the
 * plan reports what each item CARRIES instead of asking anyone to tick 193 ONUs.
 */
export const orgMigrationKindSchema = z.enum([
  'olt',
  'zone',
  'splitter',
  'optical_splitter',
  'site',
  'speed_profile',
  'onu_type',
  'authorize_preset',
  'device_preset',
]);
export type OrgMigrationKind = z.infer<typeof orgMigrationKindSchema>;

/** Singular and plural, in the operator's vocabulary rather than the table's. */
export const ORG_MIGRATION_KIND_LABELS: Record<OrgMigrationKind, { one: string; many: string }> = {
  olt: { one: 'OLT', many: 'OLTs' },
  zone: { one: 'zone', many: 'Zones' },
  splitter: { one: 'housing', many: 'ODFs, enclosures and FATs' },
  optical_splitter: { one: 'optical splitter', many: 'Optical splitters' },
  site: { one: 'gateway', many: 'Gateways' },
  speed_profile: { one: 'speed profile', many: 'Speed profiles' },
  onu_type: { one: 'ONU type', many: 'ONU types' },
  authorize_preset: { one: 'authorise preset', many: 'Authorise presets' },
  device_preset: { one: 'device preset', many: 'Device presets' },
};

/** The order the checkbox tree groups them in: equipment first, then products. */
export const ORG_MIGRATION_KIND_ORDER: readonly OrgMigrationKind[] = [
  'olt',
  'site',
  'zone',
  'splitter',
  'optical_splitter',
  'speed_profile',
  'onu_type',
  'authorize_preset',
  'device_preset',
];

export const orgMigrationItemRefSchema = z.object({
  kind: orgMigrationKindSchema,
  id: z.string().min(1).max(64),
});
export type OrgMigrationItemRef = z.infer<typeof orgMigrationItemRefSchema>;

/** One dependent set, counted. "193 ONUs", "48 PON ports". */
export const orgMigrationCarrySchema = z.object({
  label: z.string(),
  count: z.number().int(),
});
export type OrgMigrationCarry = z.infer<typeof orgMigrationCarrySchema>;

/**
 * Why an item cannot move.
 *
 * Every one of these is a refusal the operator can ACT on — widen the
 * selection, rename a profile, stop a run — rather than a wall. That is the
 * difference between a refusal and a failure, and it is why the code travels
 * with a sentence naming the thing in the way.
 */
export const orgMigrationRefusalCodeSchema = z.enum([
  /** The id is not in the source organization at all (or no longer exists). */
  'foreign',
  /** The target organization already has a row this one's unique index clashes with. */
  'name_collision',
  /** Its parent — the splitter above it, the zone above it — is staying behind. */
  'orphaned_parent',
  /** Something below it is staying behind, and would be orphaned by the move. */
  'orphaned_child',
  /** A live run on this chassis. Moving rows out from under an executor is the incident. */
  'busy',
  /** The row moved, was deleted, or changed hands between the plan and the apply. */
  'changed',
]);
export type OrgMigrationRefusalCode = z.infer<typeof orgMigrationRefusalCodeSchema>;

export const orgMigrationRefusalSchema = z.object({
  code: orgMigrationRefusalCodeSchema,
  /** A sentence naming what is in the way, by name. Never a constraint error. */
  message: z.string(),
});
export type OrgMigrationRefusal = z.infer<typeof orgMigrationRefusalSchema>;

export const orgMigrationItemPlanSchema = z.object({
  kind: orgMigrationKindSchema,
  id: z.string(),
  /** The row's own name, as the operator knows it. */
  name: z.string(),
  /** `/olts/3` — the number, where the kind has one. */
  number: z.number().int().nullable(),
  /** Vendor and model, an address, a capacity: whatever tells two rows apart. */
  detail: z.string().nullable(),
  /** Everything that travels with it, counted. Empty for a row that owns nothing. */
  carries: orgMigrationCarrySchema.array(),
  /** Non-empty means it does NOT move, and each entry says why. */
  refusals: orgMigrationRefusalSchema.array(),
  /** Consequences that do not stop the move, and that the apply will report back. */
  warnings: z.string().array(),
  movable: z.boolean(),
});
export type OrgMigrationItemPlan = z.infer<typeof orgMigrationItemPlanSchema>;

export const orgMigrationOrgRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
});
export type OrgMigrationOrgRef = z.infer<typeof orgMigrationOrgRefSchema>;

export const orgMigrationPlanSchema = z.object({
  id: z.string(),
  source: orgMigrationOrgRefSchema,
  target: orgMigrationOrgRefSchema,
  /** Every item asked for, in the order the tree shows them — refused ones included. */
  items: orgMigrationItemPlanSchema.array(),
  movableCount: z.number().int(),
  refusedCount: z.number().int(),
  /** Rows the apply would restamp or carry, summed across every movable item. */
  rowCount: z.number().int(),
  /** Consequences of the whole run, not of one item. */
  warnings: z.string().array(),
  /**
   * What would STILL belong to the source organization when this finished.
   *
   * The delete decision is made from this list, so it is computed here rather
   * than left for the operator to work out from two pages of counts.
   */
  remaining: orgAttachmentSchema.array(),
  createdAt: z.number().int(),
  expiresAt: z.number().int(),
});
export type OrgMigrationPlan = z.infer<typeof orgMigrationPlanSchema>;

export const planOrgMigrationSchema = z.object({
  targetOrgId: z.string().min(1).max(64),
  items: orgMigrationItemRefSchema.array().min(1).max(5_000),
});
export type PlanOrgMigrationRequest = z.infer<typeof planOrgMigrationSchema>;

/**
 * What happens to the organization the equipment came FROM.
 *
 * `archive` is the recommended one and the reason this enum exists at all: it
 * suspends the org AND stamps a purge date sixty days out, so "we are done with
 * this tenant" stops being a choice between leaving clutter forever and
 * destroying a record nobody can get back. It is reversible for the whole sixty
 * days — see `POST /orgs/:id/migration/unarchive`. An archive nobody can undo is
 * a delete with a delay.
 */
export const orgSourceDispositionSchema = z.enum(['keep', 'archive', 'delete_now']);
export type OrgSourceDisposition = z.infer<typeof orgSourceDispositionSchema>;

/** How long an archived organization sits before the scheduled purge takes it. */
export const ORG_ARCHIVE_GRACE_DAYS = 60;
export const ORG_ARCHIVE_GRACE_MS = ORG_ARCHIVE_GRACE_DAYS * 24 * 60 * 60 * 1_000;

/**
 * Which rungs of the confirmation ladder this run has to climb.
 *
 * Computed from the disposition, in shared code, so the form renders exactly
 * the fields the server will insist on. The server does not trust that — it
 * recomputes the same function from the request it received and refuses on any
 * gap, which is what stops a direct caller deleting a company with a plan id
 * and a number.
 */
export interface OrgMigrationConfirmation {
  /** Always. The number of movable items, typed back — an operator who types it has read it. */
  count: boolean;
  /** Always. This hands a fleet to another tenant; the receiving tenant is named. */
  targetName: boolean;
  /** The source organization's own name. Required the moment the source stops being left alone. */
  sourceName: boolean;
  /** A word matched in full. Null when the source is simply kept. */
  phrase: string | null;
}

/** The word each disposition is confirmed with. Different acts, different words. */
export const ORG_DISPOSITION_PHRASE: Record<OrgSourceDisposition, string | null> = {
  keep: null,
  archive: 'ARCHIVE',
  delete_now: 'DELETE',
};

export function orgMigrationConfirmation(
  disposition: OrgSourceDisposition,
): OrgMigrationConfirmation {
  return {
    count: true,
    targetName: true,
    sourceName: disposition !== 'keep',
    phrase: ORG_DISPOSITION_PHRASE[disposition],
  };
}

export const applyOrgMigrationSchema = z.object({
  planId: z.string().min(1).max(64),
  /** The plan's own movable count, typed back. Compared against a fresh count at write time. */
  confirmCount: z.number().int().min(0).max(100_000),
  /** The receiving organization's exact name. Trimmed, compared literally. */
  confirmTargetName: z.string().max(200).nullable().default(null),
  sourceDisposition: orgSourceDispositionSchema.default('keep'),
  /** The source organization's exact name. Required for archive and delete_now. */
  confirmSourceName: z.string().max(200).nullable().default(null),
  /** ARCHIVE or DELETE, in full. Required for archive and delete_now. */
  confirmPhrase: z.string().max(64).nullable().default(null),
  /** Why. Kept on the org as its status note, so the archive can be explained later. */
  note: z.string().max(400).nullable().default(null),
});
export type ApplyOrgMigrationRequest = z.infer<typeof applyOrgMigrationSchema>;

export const orgMigrationOutcomeSchema = z.enum(['moved', 'skipped', 'failed']);
export type OrgMigrationOutcome = z.infer<typeof orgMigrationOutcomeSchema>;

export const orgMigrationItemResultSchema = z.object({
  kind: orgMigrationKindSchema,
  id: z.string(),
  name: z.string(),
  outcome: orgMigrationOutcomeSchema,
  /** Why it was skipped, or how it failed. Null on a clean move. */
  message: z.string().nullable(),
  /** What actually changed hands — counted at write time, not copied from the plan. */
  moved: orgMigrationCarrySchema.array(),
  /** Links cleared, things detached: the consequences, after the fact. */
  warnings: z.string().array(),
});
export type OrgMigrationItemResult = z.infer<typeof orgMigrationItemResultSchema>;

export const orgMigrationResultSchema = z.object({
  planId: z.string(),
  source: orgMigrationOrgRefSchema,
  target: orgMigrationOrgRefSchema,
  /**
   * Every item, with its own outcome.
   *
   * Partial completion is reported HERE, item by item, and never rolled back
   * silently: an OLT that moved stays moved even when the preset after it was
   * refused, because undoing a successful move to tidy up a report is how an
   * operator ends up with a fleet in a state nobody described.
   */
  items: orgMigrationItemResultSchema.array(),
  movedCount: z.number().int(),
  skippedCount: z.number().int(),
  failedCount: z.number().int(),
  sourceDisposition: orgSourceDispositionSchema,
  /** What happened to the source organization, in a sentence. */
  sourceOutcome: z.string(),
  /** Set when the source was archived: the instant the scheduled purge takes it. */
  purgeAfter: z.number().int().nullable(),
  /** Still attached to the source — what a refused `delete_now` names. */
  remaining: orgAttachmentSchema.array(),
  warnings: z.string().array(),
});
export type OrgMigrationResult = z.infer<typeof orgMigrationResultSchema>;

/** One row an operator may tick, with the counts the tree prints beside it. */
export const orgMigrationCandidateSchema = z.object({
  kind: orgMigrationKindSchema,
  id: z.string(),
  name: z.string(),
  number: z.number().int().nullable(),
  detail: z.string().nullable(),
  carries: orgMigrationCarrySchema.array(),
  /** The row above it within the same kind, so the tree nests and an orphan is visible. */
  parentId: z.string().nullable(),
});
export type OrgMigrationCandidate = z.infer<typeof orgMigrationCandidateSchema>;

export const orgMigrationGroupSchema = z.object({
  kind: orgMigrationKindSchema,
  label: z.string(),
  items: orgMigrationCandidateSchema.array(),
});
export type OrgMigrationGroup = z.infer<typeof orgMigrationGroupSchema>;

export const orgMigrationCandidatesSchema = z.object({
  org: orgMigrationOrgRefSchema,
  groups: orgMigrationGroupSchema.array(),
  /** Everything the org holds, whether or not this feature can move it. */
  holds: orgAttachmentSchema.array(),
  /** Set while this org is archived: when the scheduled purge will take it. */
  purgeAfter: z.number().int().nullable(),
});
export type OrgMigrationCandidates = z.infer<typeof orgMigrationCandidatesSchema>;

/** "2 OLTs, 1 zone and 4 speed profiles" — the phrasing every refusal here uses. */
export function listCounted(parts: Array<{ label: string; count: number }>): string {
  const words = parts
    .filter((p) => p.count > 0)
    .map((p) => `${p.count} ${p.label}${p.count === 1 ? '' : 's'}`);
  if (words.length === 0) return 'nothing';
  if (words.length === 1) return words[0]!;
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}
